"use client";
import Image from "next/image";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { CustomLink } from "@/components/CustomLink";
import type { ContactBubbleElement } from "./ContactBubble.client";
import type { ExtendedLinkType } from "@/components/ExtededLink/ExtendedLink";
import styles from "./ContactBubble.module.scss";

interface ContactBubbleRowProps {
  element: ContactBubbleElement;
  onOpenModal: () => void;
}

type ResolvedLink = {
  href: string;
  target?: string;
  ariaLabel?: string;
  label: string;
};

const getExtendedLinkHref = (
  linkExtended: ExtendedLinkType,
): ResolvedLink | null => {
  switch (linkExtended.type) {
    case "link": {
      const { link } = linkExtended;

      return link && link.url
        ? {
            href: link.url,
            target: link.target,
            ariaLabel: link.aria_label,
            label: link.title,
          }
        : null;
    }
    case "telephone": {
      const { telephone } = linkExtended;

      return telephone?.link_target
        ? {
            href: `tel:${telephone.link_target}`,
            ariaLabel: telephone.link_aria_label,
            label: telephone.link_text,
          }
        : null;
    }
    case "email": {
      const { email } = linkExtended;

      return email?.link_target
        ? { href: `mailto:${email.link_target}`, label: email.link_text }
        : null;
    }
    default:
      return null;
  }
};

export const ContactBubbleRow = ({
  element,
  onOpenModal,
}: ContactBubbleRowProps) => {
  const { getLabel } = useGlobalContext();
  const rowClassName = styles["row-link"];

  const icon = element.icon?.url && (
    <Image
      src={element.icon.url}
      width={28}
      height={28}
      alt=""
      className={styles["row-icon"]}
    />
  );

  if (element.type === "booking") {
    const { bookingLink } = element;

    return (
      <CustomLink
        href={bookingLink.url}
        target={bookingLink.target || "_self"}
        aria-label={bookingLink.aria_label}
        className={rowClassName}
      >
        {icon}
        <span>{bookingLink.title}</span>
      </CustomLink>
    );
  }

  if (element.type === "link") {
    const resolved = getExtendedLinkHref(element.linkExtended);

    if (!resolved) return null;

    return (
      <CustomLink
        href={resolved.href}
        target={resolved.target || "_self"}
        aria-label={resolved.ariaLabel}
        className={rowClassName}
      >
        {icon}
        <span>{resolved.label}</span>
      </CustomLink>
    );
  }

  return (
    <button type="button" className={rowClassName} onClick={onOpenModal}>
      {icon}
      <span>{getLabel("send_request", "Anfrage senden")}</span>
    </button>
  );
};
